// ════════════════════════════════════════════════════════════════════════════
// src/pages/ConfiguracionAuditoriaExterna.jsx
// Pantalla para configurar la exportación de logs de auditoría
// ════════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import '../styles/ConfiguracionAuditoriaExterna.css';

// ✅ DEFINIR API_URL CON FALLBACK
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3100/api';

const ConfiguracionAuditoriaExterna = () => {
  const [config, setConfig] = useState({
    habilitado: false,
    formato: 'json',
    modo_async: true,
    
    webhook_habilitado: false,
    webhook_url: '',
    webhook_secret: '',
    webhook_timeout: 5000,
    
    syslog_habilitado: false,
    syslog_host: '',
    syslog_port: 514,
    syslog_protocol: 'UDP',
    
    file_habilitado: false,
    file_path: '',
    file_rotate: true
  });

  const [formatos, setFormatos] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [probando, setProbando] = useState(false);

  // Cargar configuración y formatos al montar
  useEffect(() => {
    cargarConfiguracion();
    cargarFormatos();
    cargarEstadisticas();
  }, []);

  // ══════════════════════════════════════════════════════════════════════════
  // FUNCIONES DE API
  // ══════════════════════════════════════════════════════════════════════════

  const cargarConfiguracion = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/auditoria-externa/config`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setConfig(response.data.configuracion);
      setCargando(false);
    } catch (error) {
      console.error('Error cargando configuración:', error);
      Swal.fire('Error', 'No se pudo cargar la configuración', 'error');
      setCargando(false);
    }
  };

  const cargarFormatos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/auditoria-externa/formatos`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setFormatos(response.data.formatos);
    } catch (error) {
      console.error('Error cargando formatos:', error);
    }
  };

  const cargarEstadisticas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/auditoria-externa/stats`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setEstadisticas(response.data.estadisticas);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  };

  const guardarConfiguracion = async () => {
    setGuardando(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/auditoria-externa/config`,
        config,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      Swal.fire({
        icon: 'success',
        title: '✅ Configuración guardada',
        text: 'Los cambios se aplicarán inmediatamente',
        timer: 2000,
        showConfirmButton: false
      });
      
      setConfig(response.data.configuracion);
      cargarEstadisticas();
    } catch (error) {
      console.error('Error guardando:', error);
      Swal.fire(
        'Error',
        error.response?.data?.detalle || 'No se pudo guardar la configuración',
        'error'
      );
    } finally {
      setGuardando(false);
    }
  };

  const probarConexion = async () => {
    setProbando(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/auditoria-externa/config/test`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const resultados = response.data.resultados;
      let html = '<div style="text-align: left;">';
      
      if (resultados.webhook) {
        html += `<p><strong>Webhook:</strong> ${
          resultados.webhook.success 
            ? '✅ Conectado' 
            : '❌ Error: ' + resultados.webhook.error
        }</p>`;
      }
      
      if (resultados.syslog) {
        html += `<p><strong>Syslog:</strong> ${
          resultados.syslog.success 
            ? '✅ Conectado' 
            : '❌ Error: ' + resultados.syslog.error
        }</p>`;
      }
      
      if (resultados.file) {
        html += `<p><strong>Archivo:</strong> ${
          resultados.file.success 
            ? '✅ Escribible' 
            : '❌ Error: ' + resultados.file.error
        }</p>`;
      }
      
      html += '</div>';
      
      Swal.fire({
        title: 'Resultados de la prueba',
        html: html,
        icon: 'info'
      });
      
    } catch (error) {
      console.error('Error probando conexión:', error);
      Swal.fire('Error', 'No se pudo probar la conexión', 'error');
    } finally {
      setProbando(false);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // HANDLERS
  // ══════════════════════════════════════════════════════════════════════════

  const handleChange = (campo, valor) => {
    setConfig({ ...config, [campo]: valor });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validaciones
    if (config.habilitado) {
      if (!config.webhook_habilitado && !config.syslog_habilitado && !config.file_habilitado) {
        Swal.fire('Error', 'Debe habilitar al menos un destino', 'error');
        return;
      }
      
      if (config.webhook_habilitado && !config.webhook_url) {
        Swal.fire('Error', 'Debe especificar la URL del webhook', 'error');
        return;
      }
      
      if (config.syslog_habilitado && !config.syslog_host) {
        Swal.fire('Error', 'Debe especificar el host de Syslog', 'error');
        return;
      }
      
      if (config.file_habilitado && !config.file_path) {
        Swal.fire('Error', 'Debe especificar la ruta del archivo', 'error');
        return;
      }
    }
    
    guardarConfiguracion();
  };

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════

  if (cargando) {
    return (
      <div className="cargando-container">
        <div className="spinner"></div>
        <p>Cargando configuración...</p>
      </div>
    );
  }

  return (
    <div className="config-auditoria-container">
      <div className="config-auditoria-header">
        <h1>🔧 Configuración de Auditoría Externa</h1>
        <p className="config-auditoria-subtitle">
          Enviar logs de auditoría a sistemas externos (SIEM, webhooks, archivos)
        </p>
      </div>

      <form onSubmit={handleSubmit} className="config-auditoria-form">
        
        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* CONFIGURACIÓN GENERAL */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        <div className="config-section">
          <h2>⚙️ Configuración General</h2>
          
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={config.habilitado}
                onChange={(e) => handleChange('habilitado', e.target.checked)}
              />
              <span className="checkbox-text">Habilitar exportación de logs</span>
            </label>
            <small className="form-help">
              Cuando está habilitado, los logs se enviarán automáticamente a los destinos configurados
            </small>
          </div>

          <div className="form-group">
            <label>Formato de logs</label>
            <select
              value={config.formato}
              onChange={(e) => handleChange('formato', e.target.value)}
              disabled={!config.habilitado}
            >
              {formatos.map(formato => (
                <option key={formato.value} value={formato.value}>
                  {formato.label}
                  {formato.recomendado && ' (Recomendado)'}
                </option>
              ))}
            </select>
            {formatos.find(f => f.value === config.formato) && (
              <small className="form-help">
                {formatos.find(f => f.value === config.formato).descripcion}
              </small>
            )}
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={config.modo_async}
                onChange={(e) => handleChange('modo_async', e.target.checked)}
                disabled={!config.habilitado}
              />
              <span className="checkbox-text">Modo asíncrono (No bloquear la aplicación)</span>
            </label>
            <small className="form-help">
              Recomendado: Los logs se envían en segundo plano sin afectar el rendimiento
            </small>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* WEBHOOK */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        <div className="config-section">
          <h2>📡 Webhook / HTTP</h2>
          
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={config.webhook_habilitado}
                onChange={(e) => handleChange('webhook_habilitado', e.target.checked)}
                disabled={!config.habilitado}
              />
              <span className="checkbox-text">Activar webhook</span>
            </label>
          </div>

          {config.webhook_habilitado && (
            <>
              <div className="form-group">
                <label>URL del webhook *</label>
                <input
                  type="url"
                  placeholder="https://tu-servidor.com/api/logs"
                  value={config.webhook_url}
                  onChange={(e) => handleChange('webhook_url', e.target.value)}
                  disabled={!config.habilitado}
                  required
                />
              </div>

              <div className="form-group">
                <label>Secreto (opcional)</label>
                <input
                  type="password"
                  placeholder="tu_secreto_123"
                  value={config.webhook_secret}
                  onChange={(e) => handleChange('webhook_secret', e.target.value)}
                  disabled={!config.habilitado}
                />
                <small className="form-help">
                  Se enviará en el header X-Webhook-Secret para validar el origen
                </small>
              </div>

              <div className="form-group">
                <label>Timeout (ms)</label>
                <input
                  type="number"
                  min="1000"
                  max="30000"
                  value={config.webhook_timeout}
                  onChange={(e) => handleChange('webhook_timeout', parseInt(e.target.value))}
                  disabled={!config.habilitado}
                />
              </div>
            </>
          )}
        </div>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* SYSLOG */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        <div className="config-section">
          <h2>🖥️ Servidor Syslog</h2>
          
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={config.syslog_habilitado}
                onChange={(e) => handleChange('syslog_habilitado', e.target.checked)}
                disabled={!config.habilitado}
              />
              <span className="checkbox-text">Activar Syslog</span>
            </label>
          </div>

          {config.syslog_habilitado && (
            <>
              <div className="form-group">
                <label>Host *</label>
                <input
                  type="text"
                  placeholder="syslog.empresa.com o 10.0.0.100"
                  value={config.syslog_host}
                  onChange={(e) => handleChange('syslog_host', e.target.value)}
                  disabled={!config.habilitado}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Puerto</label>
                  <input
                    type="number"
                    min="1"
                    max="65535"
                    value={config.syslog_port}
                    onChange={(e) => handleChange('syslog_port', parseInt(e.target.value))}
                    disabled={!config.habilitado}
                  />
                </div>

                <div className="form-group">
                  <label>Protocolo</label>
                  <select
                    value={config.syslog_protocol}
                    onChange={(e) => handleChange('syslog_protocol', e.target.value)}
                    disabled={!config.habilitado}
                  >
                    <option value="UDP">UDP (Rápido)</option>
                    <option value="TCP">TCP (Confiable)</option>
                  </select>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* ARCHIVO LOCAL */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        <div className="config-section">
          <h2>📁 Archivo Local</h2>
          
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={config.file_habilitado}
                onChange={(e) => handleChange('file_habilitado', e.target.checked)}
                disabled={!config.habilitado}
              />
              <span className="checkbox-text">Activar archivo local</span>
            </label>
          </div>

          {config.file_habilitado && (
            <>
              <div className="form-group">
                <label>Ruta del archivo *</label>
                <input
                  type="text"
                  placeholder="/var/log/auditoria/audit.log"
                  value={config.file_path}
                  onChange={(e) => handleChange('file_path', e.target.value)}
                  disabled={!config.habilitado}
                  required
                />
                <small className="form-help">
                  Linux: /var/log/auditoria/audit.log | Windows: C:/logs/audit.log
                </small>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={config.file_rotate}
                    onChange={(e) => handleChange('file_rotate', e.target.checked)}
                    disabled={!config.habilitado}
                  />
                  <span className="checkbox-text">Rotar archivo automáticamente</span>
                </label>
                <small className="form-help">
                  El archivo se rotará automáticamente cuando alcance 10 MB
                </small>
              </div>
            </>
          )}
        </div>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* ESTADÍSTICAS */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        {estadisticas && (
          <div className="config-section stats-section">
            <h2>📊 Estadísticas</h2>
            
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">{estadisticas.stats?.sent || 0}</div>
                <div className="stat-label">Logs enviados</div>
              </div>
              
              <div className="stat-card">
                <div className="stat-value">{estadisticas.stats?.failed || 0}</div>
                <div className="stat-label">Fallos</div>
              </div>
              
              <div className="stat-card">
                <div className="stat-value">
                  {estadisticas.enabled ? '✅ Activo' : '⏸️ Inactivo'}
                </div>
                <div className="stat-label">Estado</div>
              </div>
            </div>

            {estadisticas.stats?.lastError && (
              <div className="alert alert-warning">
                <strong>Último error:</strong> {estadisticas.stats.lastError}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* BOTONES */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        <div className="config-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={probarConexion}
            disabled={!config.habilitado || probando || guardando}
          >
            {probando ? 'Probando...' : '🧪 Probar Conexión'}
          </button>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={guardando || probando}
          >
            {guardando ? 'Guardando...' : '💾 Guardar Configuración'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ConfiguracionAuditoriaExterna;